import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { WorkoutsSignupForm } from '../components/WorkoutsSignup';
import '../styles/workouts.css';
import '../styles/sales25.css';
import '../styles/p25.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ===== CONFIG — swap these anytime =====
// Optional override: if set, every CTA links straight to this URL (e.g. a Stripe Payment Link)
const PURCHASE_URL = '';
// Artwork
const HERO_ART = 'https://res.cloudinary.com/da3ouwgdr/image/upload/v1791315648/25_for_25_uf1o87.png';
const FINAL_ART = '/images/25for25/final_art.jpg';
const WHY_PHOTOS = [
  { src: '/images/25for25/why_1.jpg', alt: 'Cal holding a plank in the grass' },
  { src: '/images/25for25/why_2.jpg', alt: 'Cal mid squat outdoors' },
  { src: '/images/25for25/why_3.jpg', alt: 'Cal mid jump, smiling' },
  { src: '/images/25for25/why_4.jpg', alt: 'Cal in a side plank reach' },
];
// App screenshots (3 slots). Leave empty strings to show the styled placeholders.
const APP_SCREENSHOTS = [
  'https://res.cloudinary.com/da3ouwgdr/image/upload/v1792452134/25for25_01_pick_kpprtb.png',
  'https://res.cloudinary.com/da3ouwgdr/image/upload/v1792452134/25for25_02_start_ccns3e.png',
  'https://res.cloudinary.com/da3ouwgdr/image/upload/v1792452135/25for25_03_move_mlhwno.png',
];

const PLACEHOLDERS = [
  { num: '01', label: 'PICK' },
  { num: '02', label: 'START' },
  { num: '03', label: 'MOVE' },
];

const ACK_TEXT = "I understand this is general fitness guidance, not medical advice. I'll check with my doctor before starting if I have any health concerns, and I take part at my own risk.";

export default function TwentyFiveSalesPage() {
  const navigate = useNavigate();
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);
  const [isPurchaser, setIsPurchaser] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAck, setShowAck] = useState(false);
  const [ackChecked, setAckChecked] = useState(false);
  const [ackError, setAckError] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    document.title = '25 for 25 — 25 workouts. 25 minutes. $25. | GoCalFit';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', '25 bodyweight-first workouts, 25 minutes or less, $25 once. No gym, no equipment, no subscription. Pick one. Hit play. Go.');

    axios.get(`${BACKEND_URL}/api/store/25-for-25/config`)
      .then(({ data }) => setCheckoutEnabled(Boolean(data.checkout_enabled)))
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/access25/session`, { withCredentials: true })
      .then(({ data }) => setIsPurchaser(Boolean(data.ok)))
      .catch(() => {});
    axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })
      .then(({ data }) => setIsAdmin((data.role || data.user?.role) === 'admin'))
      .catch(() => {});
  }, []);

  const handleCta = useCallback((e) => {
    if (PURCHASE_URL) return; // plain link, let it navigate
    e.preventDefault();
    if (isPurchaser) {
      navigate('/workouts/access');
    } else if (checkoutEnabled || isAdmin) {
      setAckChecked(false);
      setAckError('');
      setShowAck(true);
    } else {
      document.getElementById('s25-waitlist')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isPurchaser, checkoutEnabled, isAdmin, navigate]);

  const startCheckout = async () => {
    if (!ackChecked) { setAckError('Please tick the box to continue.'); return; }
    setPaying(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/store25/checkout`,
        { ack: true, origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.url;
    } catch (err) {
      setAckError(err.response?.data?.detail || 'Checkout could not start. Please try again.');
      setPaying(false);
    }
  };

  const ctaLabel = (label) => (isPurchaser ? 'Open Your Workouts' : label);

  const Cta = ({ label, testid }) => (
    <a
      className="s25-cta"
      href={PURCHASE_URL || '#s25-waitlist'}
      onClick={handleCta}
      data-testid={testid}
    >
      {ctaLabel(label)} <span className="s25-cta-arrow">→</span>
    </a>
  );

  return (
    <div className="s25" data-testid="sales25-page">
      {/* top bar */}
      <div className="s25-topbar">
        <Link to="/workouts" className="s25-home-link" data-testid="sales25-home-link">
          <ArrowLeft size={14} /> GoCalFit
        </Link>
        <div className="s25-topbar-mark">25 <span>FOR</span> 25</div>
      </div>

      {/* 1 — HERO */}
      <section className="s25-hero" data-testid="sales25-hero">
        <div>
          <p className="s25-hero-kicker">A digital workout pack by Cal</p>
          <h1>25 workouts.<br />25 minutes.<br /><em>$25.</em></h1>
          <p className="s25-hero-sub">For the days you can't get your full workout in, and have absolutely no desire to figure out what to do instead.</p>
          <p className="s25-hero-support">
            <strong>25 minutes still counts.</strong><br />
            No gym. No equipment. No zero days.<br />
            <strong>Pick one. Hit play. Go.</strong>
          </p>
          <Cta label="Get All 25 — $25" testid="sales25-cta-hero" />
        </div>
        <div className="s25-hero-art">
          <img src={HERO_ART} alt="25 for 25 — athletic artwork with an aerialist reaching skyward over a bold 25" loading="eager" />
        </div>
      </section>

      {/* 2 — SHOW THE PRODUCT */}
      <section className="s25-section s25-product" data-testid="sales25-product">
        <div className="s25-inner">
          <h2 className="s25-h2">25 in your pocket.</h2>
          <div className="s25-steps" aria-label="How it works">
            <span className="s25-step">Pick your workout</span>
            <span className="s25-step-arrow">→</span>
            <span className="s25-step">Hit start</span>
            <span className="s25-step-arrow">→</span>
            <span className="s25-step">Follow the timer</span>
          </div>
          <div className="s25-shots">
            {PLACEHOLDERS.map((ph, i) => (
              <div className="s25-shot" key={ph.label}>
                {APP_SCREENSHOTS[i] ? (
                  <img src={APP_SCREENSHOTS[i]} alt={`25 for 25 workout app — ${ph.label.toLowerCase()} screen`} loading="lazy" />
                ) : (
                  <div className="s25-shot-ph" aria-hidden="true">
                    <span className="ph-num">{ph.num}</span>
                    <span className="ph-label">{ph.label}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="s25-product-lines">
            The workout is already built.<br />
            The movements are demonstrated.<br />
            The intervals are set.<br />
            The timer keeps you moving.<br />
            <strong>Your only job is to do it.</strong>
          </p>
          <Cta label="25 Workouts in Your Pocket — $25" testid="sales25-cta-product" />
        </div>
      </section>

      {/* 3 — WHY I MADE IT */}
      <section className="s25-section s25-why" data-testid="sales25-why">
        <div className="s25-inner">
          <div className="s25-why-collage" data-testid="sales25-why-collage">
            {WHY_PHOTOS.map((p, i) => (
              <div className={`s25-why-frame s25-why-frame-${i + 1}`} key={p.src}>
                <img src={p.src} alt={p.alt} loading="lazy" />
              </div>
            ))}
          </div>
          <div>
            <h2 className="s25-h2">This is the stuff I come back to.</h2>
            <p>My own "fit stack" has always been a mix — running, aerial, bungee, P90X3, Peloton, weights & bands, skating; whatever keeps me moving.</p>
            <p>I like variety. But I always come back to the basics.<br /><strong>Squat. Push up. Lunge. Plank. Jump. Move.</strong></p>
            <div className="s25-chant">
              <span>Not because they're new & shiny.</span>
              <span className="chant-done">Because they work.</span>
            </div>
            <p>And when I'm tapped out, busy, short on time or simply don't want to decide what to do, removing that decision is sometimes the difference between moving and not moving.</p>
            <p>That's what 25 FOR 25 is. Something ready to go without thinking. Or overthinking!</p>
            <p><strong>Pick one. Give it what you've got for 25 minutes. Done.</strong></p>
          </div>
        </div>
      </section>

      {/* 4 — WHAT YOU GET */}
      <section className="s25-section s25-get" data-testid="sales25-get">
        <div className="s25-inner">
          <h2 className="s25-h2">What you get</h2>
          <ul className="s25-get-list">
            <li>25 complete workouts</li>
            <li>25 minutes or less</li>
            <li>Bodyweight-first</li>
            <li>No gym required</li>
            <li>No equipment required</li>
            <li>Movement demonstrations</li>
            <li>Built-in workout timer</li>
            <li>Warm-up + workout + cooldown</li>
          </ul>
          <div className="s25-formats" aria-label="Workout formats">
            <span className="s25-format">HIIT</span>
            <span className="s25-format">EMOM</span>
            <span className="s25-format">LADDERS</span>
            <span className="s25-format">STRENGTH + CONDITIONING</span>
          </div>
          <p className="s25-formats-note">
            Full-body workouts plus different focuses, so you can choose based on what you want that day.
          </p>
          <Cta label="No Planning. Just Press Play. — $25" testid="sales25-cta-get" />
        </div>
      </section>

      {/* 5 — PHILOSOPHY */}
      <section className="s25-section s25-philosophy" data-testid="sales25-philosophy">
        <div className="s25-inner">
          <h2 className="s25-big">You don't need more fitness information.</h2>
          <h2 className="s25-big s25-big-second">You need something you'll actually do.</h2>
          <div className="s25-philosophy-copy">
            <p>You've done these movements before. Probably hundreds of times.</p>
            <p>There's no secret exercise hiding on the internet. You could absolutely go to YouTube, Instagram or any AI platform and build yourself a workout for free.</p>
            <p>This saves you from having to.</p>
            <p>Sometimes the hardest part of working out is deciding what the hell to do.</p>
            <p><strong>25 FOR 25 removes the decision.</strong></p>
            <p className="s25-go">Open it. Pick one. Hit play. <em>Move.</em></p>
          </div>
        </div>
      </section>

      {/* 7 — FINAL CTA */}
      <section className="s25-section s25-final" data-testid="sales25-final">
        <div className="s25-inner">
          <div className="s25-final-art">
            <img src={FINAL_ART} alt="25 for 25 product artwork" loading="lazy" />
          </div>
          <div>
            <h2>25 workouts.<br />25 minutes.<br /><span className="final-price">$25.</span><br />Yours to keep.</h2>
            <p className="s25-final-sub">25 in your pocket. Pick one. Hit play. Go.</p>
            <Cta label="Get 25 for 25 — $25" testid="sales25-cta-final" />
            <div className="s25-signoff s25-final-note">
              <p>I know what 25 minutes of movement does for me. I anecdotally feel better on the other side of it. And I bet you will too. And I bet you already know that! lol</p>
              <p>Will this alone get you abs, completely transform your body or solve every fitness goal? Probably not (but reach out directly if that's what you're looking for!)</p>
              <p>It's 25 workouts for $25. 25 minutes after you start you're finished and on with your day. Your body feels it. Your brain gets the win.</p>
              <p>This gives you a simple way to move when time and mental bandwidth are in short supply.</p>
              <p>And let's be honest. If you are in midlife with family + a career, these two things are always in short supply!</p>
            </div>
            <p className="s25-signoff">Do life.<br />Come back when you've got another 25.</p>
          </div>
        </div>
      </section>

      {/* EMAIL CAPTURE — waitlist while closed, soft capture once live */}
      {!isPurchaser && (
        <section className="s25-section s25-waitlist" id="s25-waitlist" data-testid="sales25-waitlist">
          <div className="s25-inner">
            {checkoutEnabled ? (
              <>
                <h2 className="s25-h2">Not today? No problem.</h2>
                <p className="s25-waitlist-sub">Drop your email and I'll keep you posted — new workouts, pop-ups and the occasional nudge to move. No spam.</p>
                <WorkoutsSignupForm
                  source="25for25-live"
                  buttonLabel="Keep Me Posted"
                  successMessage="You're in — I'll keep you posted."
                  testPrefix="sales25"
                />
              </>
            ) : (
              <>
                <h2 className="s25-h2">It's almost ready.</h2>
                <p className="s25-waitlist-sub">Drop your email and you'll be first in line the day it opens. No spam — just the one email that matters.</p>
                <WorkoutsSignupForm
                  source="25for25"
                  buttonLabel="Put Me First in Line"
                  successMessage="You're on the list — we'll email you the moment it opens."
                  testPrefix="sales25"
                />
              </>
            )}
          </div>
        </section>
      )}

      <footer className="s25-footer">
        A GoCalFit product · <Link to="/">gocalfit.com</Link>
      </footer>

      {/* acknowledgement modal before checkout */}
      {showAck && (
        <div className="p25-ack-overlay" data-testid="ack-modal" onClick={() => setShowAck(false)}>
          <div className="p25-ack" onClick={e => e.stopPropagation()}>
            <h3>One quick thing before checkout</h3>
            <label className="p25-check">
              <input type="checkbox" checked={ackChecked} onChange={e => setAckChecked(e.target.checked)} data-testid="ack-checkbox" />
              {ACK_TEXT}
            </label>
            {ackError && <p className="p25-err" data-testid="ack-error">{ackError}</p>}
            <div className="p25-ack-actions">
              <button className="p25-btn" onClick={startCheckout} disabled={paying} data-testid="ack-continue">
                {paying ? 'Opening Stripe…' : 'Continue to payment'}
              </button>
              <button className="p25-ack-cancel" onClick={() => setShowAck(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* sticky mobile purchase bar */}
      <a
        className="s25-sticky"
        href={PURCHASE_URL || '#s25-waitlist'}
        onClick={handleCta}
        data-testid="sales25-sticky-bar"
        aria-label="Get 25 for 25 for $25"
      >
        <span className="s25-sticky-label">25 <span>FOR</span> 25 · $25</span>
        <span className="s25-sticky-arrow">→</span>
      </a>
    </div>
  );
}
